// Classified-file panel shown when an application window is not open.
import { Link } from "react-router-dom";
import { windowState } from "@/lib/windows";
import { MissionCountdown } from "@/components/recruitment/MissionCountdown";
import { TransmissionListSignup } from "@/components/recruitment/TransmissionListSignup";
import { CountdownShareCard } from "@/components/recruitment/CountdownShareCard";

const COPY = {
  student: {
    stamp: "APPLICATIONS SEALED",
    line: (w) => `Reopening ${w.opensLabel}`,
    caseNo: "BLV-2027 / STUDENT-CREATIVE",
    chips: (w) => ["RECRUITMENT WINDOW CLOSED", "FILE ACCESS: READ ONLY", `NEXT TRANSMISSION: ${w.opensShort}`],
    prepare: [
      "Review eligibility, departments, and expectations in the mission brief",
      "Choose your creative lane and gather 2–3 pieces you're proud of",
      "Get a portfolio or social link ready (a phone gallery counts)",
      "Think about your availability and what you want to build",
    ],
    cta: { label: "Review the Mission Brief", to: "/cohort", testId: "sealed-brief-btn" },
  },
  talent: null, // uses student copy
  brand: {
    stamp: "NETWORK ACCESS RESTRICTED",
    line: () => "Brand + Mentor Applications Open November 1",
    caseNo: "BLV-2027 / NETWORK-BRAND",
    chips: (w) => ["FILE ACCESS: READ ONLY", `NEXT TRANSMISSION: ${w.opensShort}`],
    prepare: [
      "Review the ways to participate — briefs, sponsorship, tools, locations, mentorship",
      "Identify a real business need a supervised student crew could take on",
      "Consider scope, timing, and any compensation you can offer",
    ],
    cta: { label: "Review Partnership Intel", to: "/brands", testId: "sealed-brief-btn" },
  },
  mentor: {
    stamp: "NETWORK ACCESS RESTRICTED",
    line: () => "Brand + Mentor Applications Open November 1",
    caseNo: "BLV-2027 / NETWORK-MENTOR",
    chips: (w) => ["FILE ACCESS: READ ONLY", `NEXT TRANSMISSION: ${w.opensShort}`],
    prepare: [
      "Review the departments and lanes in the mission brief",
      "Think about how many hours a month you can give the next wave",
      "Note the crafts and career lessons you're best placed to pass on",
    ],
    cta: { label: "Review the Mission Brief", to: "/cohort", testId: "sealed-brief-btn" },
  },
};

export const SealedNotice = ({ kind }) => {
  const w = windowState(kind);
  const c = COPY[kind] || COPY.student;
  const closedForGood = w.status === "closed";
  return (
    <div className="gold-frame grain-dark relative bg-charcoal p-8 text-center sm:p-12" data-testid={`sealed-notice-${kind}`}>
      <div className="pointer-events-none absolute inset-2 border border-gold/15" />
      <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
      <div className="relative">
        <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-silver/40">Case file {c.caseNo}</p>
        <div className="mt-5 inline-block -rotate-3 border-2 border-gold px-5 py-2.5 shadow-[0_0_30px_rgba(201,162,39,0.2)]">
          <span className="border border-gold/40 px-3 py-1 font-display text-2xl uppercase tracking-[0.15em] text-gold sm:text-3xl" data-testid="sealed-stamp">
            {closedForGood ? "RECRUITMENT WINDOW CLOSED" : c.stamp}
          </span>
        </div>
        <p className="mt-5 font-cinzel text-xs uppercase tracking-[0.35em] text-cream sm:text-sm" data-testid="sealed-line">
          {closedForGood ? "This transmission window has ended." : c.line(w)}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {(closedForGood ? ["FILE ACCESS: READ ONLY", "AWAITING NEXT DIRECTIVE"] : c.chips(w)).map((chip) => (
            <span key={chip} className="border border-silver/30 bg-onyx px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-silver/70">{chip}</span>
          ))}
        </div>
        {!closedForGood && (
          <div className="mt-8">
            <MissionCountdown kind={kind} label={kind === "brand" || kind === "mentor" ? "Network access opens in" : "Recruitment window opens in"} />
            <TransmissionListSignup />
          </div>
        )}
        <div className="gold-rule mx-auto mt-7 w-40" />
        <div className="mx-auto mt-6 max-w-md text-left">
          <p className="font-cinzel text-[10px] uppercase tracking-[0.4em] text-gold">Prepare your file</p>
          <ul className="mt-3 space-y-2">
            {c.prepare.map((p) => (
              <li key={p} className="flex gap-2.5 font-body text-sm leading-relaxed text-silver/80">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to={c.cta.to} data-testid={c.cta.testId}
            className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform duration-200 hover:-translate-y-0.5">
            {c.cta.label}
          </Link>
          <Link to="/" className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
            Return to the Village
          </Link>
        </div>
        {!closedForGood && (
          <div className="mt-12 border-t border-gold/15 pt-10">
            <p className="font-cinzel text-[10px] uppercase tracking-[0.4em] text-gold">Spread the word</p>
            <p className="mx-auto mt-2 max-w-sm font-body text-sm leading-relaxed text-silver/70">
              Download the countdown card and post it. Every recruit counts.
            </p>
            <div className="mt-6">
              <CountdownShareCard kind={kind} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
